// eslint-disable-next-line no-restricted-imports
import { useTheme } from '@emotion/react';
import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import {
  useBrandSpecialQualifyingStatus,
  useStoreSpecials,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  Box,
  Button,
  Flex,
  Loading,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import { useCallback, useMemo, useState } from 'react';
import {
  useLocation,
  useMatches,
  useParams,
  useSearchParams,
} from 'react-router';

import { AVAILABLE_WEIGHTS } from '@jane/search/util';
import {
  useAddCartProduct,
  useGetStoreSpecials,
} from '@jane/shared-ecomm/data-access';
import { contrastingColor } from '@jane/shared-ecomm/util';
import { useGetStore } from '@jane/shared/data-access';
import { useNavigateBack } from '@jane/shared/hooks';
// eslint-disable-next-line no-restricted-imports
import type { BrandSpecial } from '@jane/shared/models';
import type { Store, StoreSpecial } from '@jane/shared/types';
import {
  availableWeightsForMenuProduct,
  calculateDiscountPercentage,
  getApplicablePriceIds,
  getIfAllPriceIdsShareDiscount,
  getMaxCartQuantity,
  getMenuPdpApplicableGoldTagLabel,
  getMenuRowWeightFilter,
  getWeightFilterForFeaturedCategory,
  lowestPriceForMenuProduct,
} from '@jane/shared/util';

import {
  Modal,
  ProductAddedToast,
  ProductRow,
  ProductWeightsGrid,
} from '../../components';
import type {
  ProductDetailLoaderType,
  RootLoaderType,
} from '../../data-access';
import { getLocalCartUuid, useGetCart } from '../../data-access';
import { useGetMaterializedMenuProduct } from '../../data-access/lib/queries/product';
import {
  useAddProductOnSuccess,
  useAddProductToBag,
  useGetProductPrices,
  useKioskSpacing,
  useRowContext,
  useShouldKioskShowGold,
  useTypedLoaderData,
  useTypedRouteLoaderData,
} from '../../hooks';
import { useBrandSpecials, useProductTracking } from '../../providers';
import type { OptionalMenuRouteParams } from '../../types';
import { isPriceId } from '../../types';
import { getSource, isSupportedStoreSpecial } from '../../util';

const useProductDetailsLoaderData = () => {
  const routeSpecificData =
    useTypedRouteLoaderData<ProductDetailLoaderType>('cartWeightModal');
  const loaderData = useTypedLoaderData<ProductDetailLoaderType>();

  return routeSpecificData === undefined ? loaderData : routeSpecificData;
};

export const AddMultiWeightPrompt = () => {
  const {
    colors: {
      primary: { main },
    },
  } = useTheme();
  const { storeId, store: initialStore } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { product: initialProduct, productId } = useProductDetailsLoaderData();
  const { data: cart = {} } = useGetCart();
  const cartUuid = getLocalCartUuid();
  const [queryParams] = useSearchParams();
  const {
    cartToppers: { addCartTopperId },
    inlineAds: { addInlineAdId },
  } = useProductTracking();
  const { brandSpecials } = useBrandSpecials();

  const { specialId, brandSpecialId } = useParams<OptionalMenuRouteParams>();
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);
  const supportedStoreSpecials = specials.filter((special) =>
    isSupportedStoreSpecial(special, true)
  );

  const specialInScope = supportedStoreSpecials.find(
    ({ id }) => `${id}` === specialId
  );
  const { pathname } = useLocation();
  const matches = useMatches();

  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });

  const { rowId, featuredCategory } = useRowContext(store);
  const menuRowWeightFilter = useMemo(
    () =>
      rowId
        ? getMenuRowWeightFilter({
            customRowSettings: store.custom_row_settings,
            rowId,
          })
        : getWeightFilterForFeaturedCategory(store, featuredCategory),
    [store, featuredCategory, rowId]
  );

  const { data: product, isLoading: isProductLoading } =
    useGetMaterializedMenuProduct(
      {
        productId,
        storeId,
      },
      { initialData: initialProduct }
    );
  const getProductPrice = useGetProductPrices(product, specials);

  const weights = availableWeightsForMenuProduct(product);

  const brandSpecial = brandSpecials.find(
    ({ id }) => id === Number(brandSpecialId)
  );
  const { data: qualifyingStatusData } = useBrandSpecialQualifyingStatus({
    cartUuid: cartUuid ?? undefined,
    specialId: brandSpecial?.id,
    storeId: store?.id,
  });

  const qualifyingStatus = qualifyingStatusData?.qualifying_status;

  const applicablePriceIds = getApplicablePriceIds(
    brandSpecial!,
    qualifyingStatus ?? 'not_qualified'
  );

  // if there are no weight restrictions, we return all of the weights
  const visiblePriceIds =
    applicablePriceIds.length !== AVAILABLE_WEIGHTS.length
      ? applicablePriceIds
      : null;

  const priceId = useMemo(() => {
    const priceParam = queryParams.get('priceId');
    if (isPriceId(priceParam)) {
      return weights.includes(priceParam) ? priceParam : null;
    }
    return null;
  }, [queryParams, weights]);

  const isInlineAd = useMemo(
    () => queryParams.get('inline')?.toLowerCase() === 'true',
    [queryParams]
  );

  const [selectedWeight, setSelectedWeight] = useState<PriceId>(
    priceId || lowestPriceForMenuProduct(product, weights) || 'each'
  );

  const { unitPrice, discountPrice } = getProductPrice(selectedWeight);
  const discountPercentage = useMemo(() => {
    return calculateDiscountPercentage(unitPrice, discountPrice);
  }, [unitPrice, discountPrice]);

  const shouldShowGold = useShouldKioskShowGold({ product });
  // Determines if Jane Gold tags are shown on the weight
  const allPricesShareDiscount = useMemo(
    () =>
      shouldShowGold &&
      getIfAllPriceIdsShareDiscount({
        menuProduct: product,
      }),
    [product, shouldShowGold]
  );

  const brandSpecialsForMenuProduct: BrandSpecial[] = (
    product.applicable_brand_special_ids ?? []
  ).flatMap((id) => {
    const special = brandSpecials.find((special) => special.id === id);
    return special ? [special] : [];
  });

  const janeGoldTagLabel = useMemo(() => {
    if (!shouldShowGold) return null;

    return getMenuPdpApplicableGoldTagLabel({
      brandSpecials: brandSpecialsForMenuProduct,
      menuProduct: product,
    });
  }, [product, shouldShowGold]);

  const { getKioskSpacing, isIpad } = useKioskSpacing();
  const goBack = useNavigateBack(`stores/${storeId}/menu`);

  const { mutate: addCartProduct, isLoading: isAddingTopper } =
    useAddCartProduct({
      onSuccess: () => {
        addCartTopperId(product.product_id);
      },
    });

  const pdpDepth = queryParams.get('pdpDepth');

  const trackDmProducts = useCallback(() => {
    if (isInlineAd) {
      addInlineAdId(product.product_id);
    }
    goBack(Number(pdpDepth) || 1);
  }, [isInlineAd, pdpDepth]);

  const onSuccess = useAddProductOnSuccess({
    callback: trackDmProducts,
    cart,
    discountPercentage,
    discountPrice,
    product,
    selectedWeight,
    source: getSource({ pathname, matches, isTopper: false }),
    store,
    unitPrice,
    quickAdd: true,
    isInlineAd,
    specialInScope,
  });

  const { onAddToBag, isLoading: isAddingMenu } = useAddProductToBag({
    cart,
    priceId: selectedWeight,
    product,
    onSuccess,
    store,
  });

  const isAdding = useMemo(
    () => isAddingTopper || isAddingMenu,
    [isAddingMenu, isAddingTopper]
  );

  const loadingColor = useMemo(() => {
    const color = contrastingColor(main, ['#0E0E0E', '#FFFFFF']);
    return color === '#FFFFFF' ? 'white' : 'black';
  }, [main]);
  const toast = useToast();

  const onAddCartTopper = useCallback(
    (weight: PriceId = selectedWeight) => {
      addCartProduct({
        app_mode: 'kiosk',
        app_mode_name: 'kiosk',
        cart_uuid: cart.uuid,
        product: {
          count: 1,
          price_id: weight,
          product_id: product.product_id,
          recommended: false,
        },
        store_id: storeId,
        user_agent: window.navigator.userAgent,
      });
      toast.add({
        label: <ProductAddedToast productCount={1} storeId={storeId} />,
      });
      goBack(Number(pdpDepth) || 1);
    },
    [
      addCartProduct,
      cart.uuid,
      goBack,
      product.product_id,
      selectedWeight,
      storeId,
      toast,
      pdpDepth,
    ]
  );
  const addToBag = useCallback(
    (weight: PriceId = selectedWeight) => {
      pathname.includes('cart')
        ? onAddCartTopper(weight)
        : onAddToBag({ weight });
    },
    [onAddCartTopper, onAddToBag, pathname, selectedWeight]
  );

  const onWeightClick = useCallback((weight: PriceId) => {
    setSelectedWeight(weight);
  }, []);

  const disableAdd = useMemo(() => {
    const existingCartProduct = (cart?.products ?? []).find(
      ({ price_id, product_id }) =>
        product.product_id === product_id && price_id === selectedWeight
    );
    return (
      isAdding ||
      (!!existingCartProduct &&
        existingCartProduct.count ===
          getMaxCartQuantity(existingCartProduct, existingCartProduct.price_id))
    );
  }, [cart?.products, isAdding, product.product_id, selectedWeight]);

  return (
    <Modal
      appId="root"
      contentLabel="Add Topper"
      onRequestClose={() => goBack()}
      open
      overflow="visible"
      variant="dialogue"
    >
      <Modal.Content overflowY="auto" padding={false}>
        <Flex
          flexDirection="column"
          p={getKioskSpacing(32, 40)}
          minWidth={isIpad ? '61vw' : '48vw'}
        >
          <Typography mb={getKioskSpacing(24, 8)} variant="title-bold">
            Select purchase option
          </Typography>
          <Box
            border="transparent-black-five"
            borderRadius="sm"
            mb={24}
            pt={24}
            px={24}
          >
            <ProductRow
              janeGoldTagLabel={janeGoldTagLabel}
              product={product}
              showPrice={false}
            />
          </Box>

          <Typography mb={16} variant="body-bold">
            Select weight
          </Typography>

          <ProductWeightsGrid
            allPricesShareDiscount={allPricesShareDiscount}
            numColumns={2}
            onWeightClick={onWeightClick}
            product={product}
            selectedWeight={selectedWeight}
            shouldShowGold={shouldShowGold}
            specials={specials}
            visiblePrideIds={visiblePriceIds ?? undefined}
            weightFilter={menuRowWeightFilter}
            weightRuleBrandSpecial={
              !janeGoldTagLabel ? brandSpecialsForMenuProduct : []
            }
          />
          <Flex flexDirection="row" mt={32}>
            <Button
              ariaLabel="cancel"
              full
              loading={isProductLoading}
              label={
                <Typography
                  color="text-main"
                  variant={`${isIpad ? 'body' : 'section'}-bold`}
                >
                  Cancel
                </Typography>
              }
              mr={getKioskSpacing(24, 16)}
              onClick={() => goBack()}
              variant="tertiary"
              size={isIpad ? 'small' : 'large'}
            />
            <Button
              ariaLabel="add product to bag"
              full
              disabled={disableAdd}
              label={
                isAdding ? (
                  <Loading color={loadingColor} size="sm" />
                ) : (
                  <Typography
                    color="text-info"
                    variant={`${isIpad ? 'body' : 'section'}-bold`}
                  >
                    Add to bag
                  </Typography>
                )
              }
              onClick={() => addToBag()}
              size={isIpad ? 'small' : 'large'}
            />
          </Flex>
        </Flex>
      </Modal.Content>
    </Modal>
  );
};
